import * as THREE from 'three';
import { Kit, place } from '../seasons/kit.ts';
import { districtMaterial, type DistrictUniforms } from './districts.ts';

/**
 * The group's ensign (docs/SABERES.md §4.5 «La enseña del grupo»): a field, a colour and a sign of a short list,
 * hung as a gonfalon from a standard on the keep, by each gate and on every tower of level 3 and up. Built like the
 * districts' heraldry (engine/districts.ts): one merged mesh painted with vertex colours, its cloth stirred in the
 * vertex shader of the same program the districts use (no new shader, nothing per frame on the CPU but one uniform),
 * rebuilt only when the look or what it hangs from changes. `ensignSvg` draws the same ensign for the editor.
 */

export interface EnsignLook {
  /** 0 plain, 1 parted, 2 a bend, 3 a cross. */
  field: number;
  /** 0 dragon red, 1 charcoal, 2 the Vigil's blue, 3 moss green, 4 slate, 5 ash. */
  color: number;
  /** 0 the Vigil's eye, 1 a boulder, 2 the shell, 3 a flame, 4 a tower, 5 a key, 6 a hammer, 7 a moon. */
  sign: number;
}

/** The ensign's colours (no gold, no beige): dragon red, charcoal, the Vigil's blue, moss green, slate, ash. */
export const ENSIGN_COLORS = ['#ff4f3a', '#2c3036', '#3f5f8a', '#4d6b3a', '#6b7480', '#c9ccd2'];

/** What goes on a field of colour `c`: ash on the dark and the red, charcoal on ash. */
const counter = (c: number): string => (c === 5 ? '#22252b' : '#e3e6ec');

/** Where one gonfalon hangs: its top's middle, the way its face looks (yaw), its width. */
export interface EnsignSpot {
  x: number;
  y: number;
  z: number;
  yaw: number;
  w: number;
  /** A standard of its own to stand on (from the ground at y0), else it hangs from what is there. */
  pole?: number;
}

const P = (() => {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const cloth = new THREE.BoxGeometry(1, 1, 1, 1, 5, 1);
  const tail = new THREE.ConeGeometry(0.5, 1, 3);
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
  const half = new THREE.CylinderGeometry(0.5, 0.5, 1, 8, 1, false, -Math.PI / 2, Math.PI);
  const cone = new THREE.ConeGeometry(0.5, 1, 6);
  const ring = new THREE.TorusGeometry(0.5, 0.14, 3, 10);
  const ball = new THREE.IcosahedronGeometry(0.5, 0);
  return { box, cloth, tail, cyl, half, cone, ring, ball };
})();

const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const WOOD = '#33261b';
const IRON = '#2c2e33';

/** Lays one gonfalon of `look` at `sp` into the kit: crossbar, cloth and tails, field, sign; a standard if asked. */
const gonfalon = (kit: Kit, look: EnsignLook, sp: EnsignSpot, ph: number): void => {
  const w = sp.w, h = w * 1.45;
  const m = place(sp.x, sp.y, sp.z, sp.yaw);
  const c = { aTop: sp.y, aPh: ph };
  const field = ENSIGN_COLORS[look.color] ?? ENSIGN_COLORS[0]!;
  const ink = counter(look.color);
  if (sp.pole !== undefined) {
    const tall = sp.y - sp.pole + 0.12;
    kit.add(P.cyl, WOOD, m, place(0, -tall / 2 + 0.12, -0.02, 0, V(0.035, tall, 0.035)));
    kit.add(P.ball, IRON, m, place(0, 0.15, -0.02, 0, 0.055));
  }
  kit.add(P.box, WOOD, m, place(0, 0.012, 0, 0, V(w + 0.08, 0.026, 0.03)));
  kit.add(P.cloth, field, m, place(0, -h / 2, 0.006, 0, V(w, h, 0.012)), c);
  for (const s of [-1, 1]) kit.add(P.tail, field, m, place(s * w * 0.25, -h - 0.05, 0.006, 0, V(w * 0.5, 0.1, 0.012), Math.PI), c);
  const z = 0.014;
  // The field's partition, in the counter colour.
  if (look.field === 1) kit.add(P.cloth, ink, m, place(w / 4, -h / 2, z, 0, V(w / 2, h, 0.008)), c);
  else if (look.field === 2) kit.add(P.box, ink, m, place(0, -h * 0.5, z, 0, V(w * 0.3, h * 1.15, 0.008), 0, -0.55), c);
  else if (look.field === 3) {
    kit.add(P.cloth, ink, m, place(0, -h / 2, z, 0, V(w * 0.2, h, 0.008)), c);
    kit.add(P.box, ink, m, place(0, -h * 0.36, z, 0, V(w, h * 0.13, 0.008)), c);
  }
  // The sign, at the middle of its upper half, over a disc of the field so it reads on any partition.
  const sy = -h * 0.36, s = w * 0.46, sz = z + 0.006;
  const sc = ink;
  if (look.field !== 0) kit.add(P.cyl, field, m, place(0, sy, sz - 0.003, 0, V(s * 1.25, 0.006, s * 1.25), Math.PI / 2), c);
  const at = (x: number, y: number, sx: number, sy2: number, rz = 0, geo: THREE.BufferGeometry = P.box, color = sc): void =>
    kit.add(geo, color, m, place(x * s, sy + y * s, sz, 0, V(sx * s, sy2 * s, 0.008), 0, rz), c);
  const disc = (x: number, y: number, r: number, color = sc): void => kit.add(P.cyl, color, m, place(x * s, sy + y * s, sz, 0, V(r * s, 0.008, r * s), Math.PI / 2), c);
  switch (look.sign) {
    case 0: // The Vigil's eye: a lens and its pupil.
      kit.add(P.cyl, sc, m, place(0, sy, sz, 0, V(s, 0.008, s * 0.5), Math.PI / 2), c);
      disc(0, 0, 0.32, field);
      disc(0, 0, 0.14);
      break;
    case 1: // A boulder on its chock.
      disc(0, 0.08, 0.7);
      at(0, -0.36, 0.8, 0.12);
      break;
    case 2: // The shell: a half disc, its plates parted.
      kit.add(P.half, sc, m, place(0, sy - 0.2 * s, sz, 0, V(s * 0.95, 0.008, s * 0.95), Math.PI / 2), c);
      for (const r of [-0.5, 0, 0.5]) at(r * 0.4, 0.0, 0.05, 0.42, r, P.box, field);
      break;
    case 3: // A flame.
      at(0, 0.05, 0.55, 0.85, 0, P.cone);
      at(0, -0.05, 0.25, 0.4, 0, P.cone, field);
      break;
    case 4: // A tower: its body and three merlons.
      at(0, -0.1, 0.42, 0.62);
      for (const x of [-0.16, 0, 0.16]) at(x, 0.28, 0.1, 0.14);
      at(0, -0.26, 0.12, 0.2, 0, P.box, field);
      break;
    case 5: // A key.
      kit.add(P.ring, sc, m, place(0, sy + 0.28 * s, sz, 0, V(s * 0.36, s * 0.36, 0.02)), c);
      at(0, -0.12, 0.09, 0.6);
      at(0.1, -0.36, 0.18, 0.07);
      at(0.08, -0.22, 0.12, 0.07);
      break;
    case 6: // A hammer.
      at(0, 0.24, 0.62, 0.2);
      at(0, -0.12, 0.1, 0.6);
      break;
    default: // A moon: a disc and a disc of the field over it.
      disc(0, 0, 0.66);
      disc(0.2, 0.08, 0.56, field);
  }
};

export class EnsignLayer {
  readonly group = new THREE.Group();
  private u: DistrictUniforms = { uRise: { value: 1 }, uUnfurl: { value: 1 }, uTime: { value: 0 }, uSway: { value: 1 } };
  private mat: THREE.MeshStandardMaterial;
  private mesh: THREE.Mesh | null = null;
  private made = '';

  constructor(low: boolean) {
    this.group.name = 'ensign';
    this.u.uSway.value = low ? 0 : 1;
    this.mat = districtMaterial(this.u);
  }

  /** Hangs `look` at `spots` (world units); the same again leaves it as it is. Null or no spots takes it down. */
  set(look: EnsignLook | null, spots: EnsignSpot[]): void {
    const made = look ? `${look.field}${look.color}${look.sign}|${spots.map((s) => `${s.x.toFixed(2)},${s.y.toFixed(2)},${s.z.toFixed(2)},${s.yaw.toFixed(2)},${s.w}`).join(';')}` : '';
    if (made === this.made) return;
    this.made = made;
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    if (!look || spots.length === 0) return;
    const kit = new Kit({ aTop: -1, aGlow: 0, aPh: 0 });
    spots.forEach((sp, i) => gonfalon(kit, look, sp, i * 2.399));
    this.mesh = kit.build(this.mat, 'ensign');
    if (this.mesh) {
      this.mesh.matrixAutoUpdate = false;
      this.group.add(this.mesh);
    }
  }

  frame(t: number): void {
    if (this.mesh) this.u.uTime.value = t;
  }
}

/**
 * The ensign as a small SVG (a gonfalon with its two tails), for the editor: the same field, colour and sign as on
 * the board. Procedural; a string to put in the page.
 */
export const ensignSvg = (field: number, color: number, sign: number): string => {
  const f = ENSIGN_COLORS[color] ?? ENSIGN_COLORS[0]!;
  const k = counter(color);
  const W = 60, H = 80;
  const cloth = `M4 4 H56 V${H - 12} L43 ${H - 2} L30 ${H - 12} L17 ${H - 2} L4 ${H - 12} Z`;
  // Its clip's id names the ensign, so several previews on one page don't take each other's.
  const id = `ensign-${field}-${color}-${sign}`;
  const parts: string[] = [`<defs><clipPath id="${id}"><path d="${cloth}"/></clipPath></defs>`, `<path d="${cloth}" fill="${f}"/>`];
  const clip = (s: string): string => `<g clip-path="url(#${id})">${s}</g>`;
  if (field === 1) parts.push(clip(`<rect x="30" y="0" width="30" height="${H}" fill="${k}"/>`));
  else if (field === 2) parts.push(clip(`<polygon points="0,10 12,0 62,62 50,74" fill="${k}"/>`));
  else if (field === 3) parts.push(clip(`<rect x="25" y="0" width="10" height="${H}" fill="${k}"/><rect x="0" y="25" width="${W}" height="9" fill="${k}"/>`));
  const cx = 30, cy = 29;
  if (field !== 0) parts.push(`<circle cx="${cx}" cy="${cy}" r="15" fill="${f}"/>`);
  const signs: string[] = [
    `<ellipse cx="${cx}" cy="${cy}" rx="13" ry="7" fill="${k}"/><circle cx="${cx}" cy="${cy}" r="4.2" fill="${f}"/><circle cx="${cx}" cy="${cy}" r="1.8" fill="${k}"/>`,
    `<circle cx="${cx}" cy="${cy - 1}" r="9" fill="${k}"/><rect x="${cx - 10}" y="${cy + 8}" width="20" height="3" fill="${k}"/>`,
    `<path d="M${cx - 12} ${cy + 5} A12 12 0 0 1 ${cx + 12} ${cy + 5} Z" fill="${k}"/><path d="M${cx} ${cy + 5} V${cy - 6} M${cx - 6} ${cy + 5} L${cx - 4} ${cy - 5} M${cx + 6} ${cy + 5} L${cx + 4} ${cy - 5}" stroke="${f}" stroke-width="1.4"/>`,
    `<path d="M${cx} ${cy - 12} C${cx + 9} ${cy - 2} ${cx + 8} ${cy + 10} ${cx} ${cy + 10} C${cx - 8} ${cy + 10} ${cx - 9} ${cy - 2} ${cx} ${cy - 12} Z" fill="${k}"/><path d="M${cx} ${cy - 1} C${cx + 4} ${cy + 3} ${cx + 3} ${cy + 8} ${cx} ${cy + 8} C${cx - 3} ${cy + 8} ${cx - 4} ${cy + 3} ${cx} ${cy - 1} Z" fill="${f}"/>`,
    `<rect x="${cx - 6}" y="${cy - 6}" width="12" height="17" fill="${k}"/><rect x="${cx - 7}" y="${cy - 10}" width="3" height="4" fill="${k}"/><rect x="${cx - 1.5}" y="${cy - 10}" width="3" height="4" fill="${k}"/><rect x="${cx + 4}" y="${cy - 10}" width="3" height="4" fill="${k}"/><rect x="${cx - 2}" y="${cy + 5}" width="4" height="6" fill="${f}"/>`,
    `<circle cx="${cx}" cy="${cy - 6}" r="5" fill="none" stroke="${k}" stroke-width="2.4"/><rect x="${cx - 1.3}" y="${cy - 1}" width="2.6" height="14" fill="${k}"/><rect x="${cx}" y="${cy + 9}" width="5" height="2.4" fill="${k}"/><rect x="${cx}" y="${cy + 5}" width="3.5" height="2.4" fill="${k}"/>`,
    `<rect x="${cx - 9}" y="${cy - 9}" width="18" height="6" fill="${k}"/><rect x="${cx - 1.5}" y="${cy - 3}" width="3" height="15" fill="${k}"/>`,
    `<circle cx="${cx}" cy="${cy}" r="10" fill="${k}"/><circle cx="${cx + 4}" cy="${cy - 2}" r="8.5" fill="${f}"/>`,
  ];
  parts.push(signs[sign] ?? signs[0]!);
  parts.push(`<rect x="1" y="1" width="58" height="4" rx="1" fill="#33261b"/>`);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
};
